import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  NgZone,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from '@angular/material/icon';
import { ButtonComponent } from 'ui-library';

export interface RiskBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** A box to render, with its label name/color (for the overlay). */
export interface DrawerBox {
  box: RiskBox;
  label?: string;
  color?: string;
  ai?: boolean;
}

@Component({
  selector: 'app-base-drawer',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, ButtonComponent],
  templateUrl: './base-drawer.component.html',
  styleUrls: ['./base-drawer.component.scss']
})
export class BaseDrawerComponent implements AfterViewInit, OnDestroy {

  @Input() imageUrl!: string;
  @Input() boxes: DrawerBox[] = [];
  @Input() activeIndex: number | null = null;
  @Input() disabled: boolean = false;
  @Input() readOnly: boolean = false;
  // Hide the floating draw/edit buttons (e.g. when the labeler is scrolled near its bottom
  // action bar on mobile, so they don't overlap the "Signaler" button).
  @Input() hideTools: boolean = false;
  @Input() pendingLabel: { label?: string; color?: string } | null = null;

  @Output() boxCreated = new EventEmitter<RiskBox>();
  @Output() boxSelected = new EventEmitter<number>();
  @Output() boxChanged = new EventEmitter<{ index: number; box: RiskBox }>();
  @Output() boxDeleted = new EventEmitter<number>();
  @Output() interactionStart = new EventEmitter<void>();
  @Output() boxOpened = new EventEmitter<number>();
  @Output() boxHovered = new EventEmitter<number>();
  @Output() boxUnhovered = new EventEmitter<number>();

  @ViewChild('imageContainer') imageContainer!: ElementRef;
  @ViewChild('imageRef') imageRef!: ElementRef<HTMLImageElement>;
  @ViewChildren('boxEl') boxEls?: QueryList<ElementRef<HTMLElement>>;

  private cdr = inject(ChangeDetectorRef);
  private readonly zone: NgZone = inject(NgZone);
  // Every pointer move of the page reaches the document: outside Angular, it only comes back in while drawing, dragging or resizing.
  private readonly pointerMove = (event: PointerEvent) => {
    if (this.isDrawing || this.drawArmed || this.isDragging || this.isResizing) this.zone.run(() => this.onPointerMove(event));
  };

  constructor() {
    this.zone.runOutsideAngular(() => document.addEventListener('pointermove', this.pointerMove));
  }

  ngOnDestroy(): void {
    document.removeEventListener('pointermove', this.pointerMove);
  }

  /** The rendered element for a box index — CDK anchor for the annotation popup. */
  getBoxElement(index: number | null): HTMLElement | null {
    if (index == null) return null;
    const fromQuery = this.boxEls?.toArray()[index]?.nativeElement;
    if (fromQuery) return fromQuery;
    // Fallback: read the live DOM (ViewChildren may not have refreshed yet across components)
    const container = this.imageContainer?.nativeElement as HTMLElement | undefined;
    const boxes = container?.querySelectorAll('.box:not(.pending)');
    return (boxes?.[index] as HTMLElement) ?? null;
  }

  // A new box being drawn (before it's committed via boxCreated)
  pending: RiskBox | null = null;
  isDrawing = false;      // mobile: drag-to-draw in progress
  drawArmed = false;      // desktop: first click placed, awaiting second click
  isDragging = false;
  isResizing = false;
  private dragIndex: number | null = null;
  private movedSinceDown = false;   // true once a drag/resize actually moves the box
  private lastTapTime = 0;          // double-tap detection (touch has no reliable dblclick)
  private lastTapIndex: number | null = null;

  startX = 0;
  startY = 0;
  resizeDir: string | null = null;
  aspectClass = "landscape";
  imageRatio = 16 / 9;
  isMobile = false;
  // Mobile only: two mutually-exclusive tools. `drawMode` (+ button) draws new boxes;
  // `editMode` (edit button) moves/resizes/deletes existing ones. With neither active,
  // a tap on a box opens its editor (view fields). Desktop ignores these (uses `disabled`).
  drawMode = false;
  editMode = false;

  ngAfterViewInit(): void {
    this.isMobile = window.innerWidth < 768;
    this.disabled = this.isMobile;
  }

  /** Can the user move/resize/delete existing boxes right now? */
  get canEditGeometry(): boolean {
    if (this.readOnly) return false;
    return this.isMobile ? this.editMode : !this.disabled;
  }

  toggleDrawMode(): void {
    this.drawMode = !this.drawMode;
    if (this.drawMode) this.editMode = false;
  }

  toggleEditMode(): void {
    this.editMode = !this.editMode;
    if (this.editMode) this.drawMode = false;
  }

  // Desktop: two-click drawing — first click arms a pending box at the point (it then follows the
  // cursor), second click commits it.
  onCanvasClick(event: MouseEvent) {
    if (this.readOnly || this.disabled || this.isMobile) return;
    if (!this.drawArmed) {
      this.armDrawing(event);
    } else {
      this.drawArmed = false;
      const p = this.pending;
      this.pending = null;
      if (p && p.width > 0.01 && p.height > 0.01) this.boxCreated.emit(p);
    }
  }

  get isDrawingBox(): boolean {
    return this.drawArmed || this.isDrawing;
  }

  onBoxClick(event: MouseEvent) {
    event.stopPropagation();
    if (this.readOnly || this.disabled || this.isMobile || this.drawArmed || !this.isAddModifier(event)) return;
    this.armDrawing(event);
  }

  private isAddModifier(event: MouseEvent): boolean {
    return event.ctrlKey || event.metaKey;
  }

  private armDrawing(event: MouseEvent) {
    const rect = this.getImageRect();
    if (!rect) return;
    this.drawArmed = true;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.pending = {
      left: (event.clientX - rect.left) / rect.width,
      top: (event.clientY - rect.top) / rect.height,
      width: 0,
      height: 0
    };
  }

  // Mobile: drag to draw (no cursor to click/hover between two points). Only in draw mode.
  onCanvasPointerDown(event: PointerEvent) {
    if (this.readOnly || !this.isMobile || !this.drawMode) return;
    const rect = this.getImageRect();
    if (!rect) return;
    this.isDrawing = true;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.pending = {
      left: (event.clientX - rect.left) / rect.width,
      top: (event.clientY - rect.top) / rect.height,
      width: 0,
      height: 0
    };
  }

  // Pointer on an existing box. Double-tap/double-click opens the editor; a single tap only
  // selects it (and, when geometry editing is allowed, begins a move-drag).
  onBoxPointerDown(event: PointerEvent, index: number) {
    event.stopPropagation();
    if (!this.isMobile && this.isAddModifier(event)) return;

    // Double-tap / double-click → open the editor. Manual detection because touch has no
    // reliable dblclick; box `touch-action: none` disables double-tap-zoom so this stays crisp.
    const now = Date.now();
    if (this.lastTapIndex === index && now - this.lastTapTime < 350) {
      this.lastTapTime = 0;
      this.lastTapIndex = null;
      this.boxOpened.emit(index);
      return;
    }
    this.lastTapTime = now;
    this.lastTapIndex = index;

    // Single tap → select only (lets the user pick a label from the list for this box).
    if (index !== this.activeIndex) this.boxSelected.emit(index);

    // Move-drag only where geometry editing is enabled (desktop, or mobile edit mode).
    if (!this.canEditGeometry) return;
    this.isDragging = true;
    this.dragIndex = index;
    this.movedSinceDown = false;
    this.startX = event.clientX;
    this.startY = event.clientY;
  }

  onBoxPointerEnter(event: PointerEvent, index: number) {
    if (event.pointerType !== 'mouse' || this.isDrawingBox || this.isDragging || this.isResizing) return;
    this.boxHovered.emit(index);
  }

  onBoxPointerLeave(event: PointerEvent, index: number) {
    if (event.pointerType !== 'mouse') return;
    this.boxUnhovered.emit(index);
  }

  onResizePointerDown(event: PointerEvent, index: number, dir: string = 'br') {
    if (!this.canEditGeometry) return;
    event.stopPropagation();
    this.isResizing = true;
    this.dragIndex = index;
    this.movedSinceDown = false;
    this.resizeDir = dir;
    this.startX = event.clientX;
    this.startY = event.clientY;
  }

  // Fired the first time a drag/resize actually moves the box (so the popup can close).
  private noteMovement() {
    if (this.movedSinceDown) return;
    this.movedSinceDown = true;
    this.interactionStart.emit();
  }

  onPointerMove(event: PointerEvent) {
    if (this.isDrawing || this.drawArmed) this.updateDrawing(event);
    else if (this.isDragging) this.updateDrag(event);
    else if (this.isResizing) this.updateResize(event);
  }

  @HostListener('document:pointerup', ['$event'])
  onPointerUp() {
    if (this.isDrawing) {
      // mobile drag-draw finished
      this.isDrawing = false;
      const p = this.pending;
      this.pending = null;
      if (p && p.width > 0.01 && p.height > 0.01) this.boxCreated.emit(p);
      this.cdr.detectChanges();
      return;
    }
    if ((this.isDragging || this.isResizing) && this.dragIndex != null) {
      const box = this.boxes[this.dragIndex]?.box;
      this.isDragging = false;
      this.isResizing = false;
      this.resizeDir = null;
      if (box) this.boxChanged.emit({ index: this.dragIndex, box });
      this.dragIndex = null;
    }
  }

  private updateDrawing(event: PointerEvent) {
    const rect = this.getImageRect();
    if (!rect || !this.pending) return;
    const x1 = (this.startX - rect.left) / rect.width;
    const y1 = (this.startY - rect.top) / rect.height;
    const x2 = Math.max(0, (event.clientX - rect.left) / rect.width);
    const y2 = Math.max(0, (event.clientY - rect.top) / rect.height);
    let left = Math.max(0, Math.min(x1, x2));
    let top = Math.max(0, Math.min(y1, y2));
    let width = Math.abs(x2 - x1);
    let height = Math.abs(y2 - y1);
    if (left + width > 1) width = 1 - left;
    if (top + height > 1) height = 1 - top;
    this.pending = { left, top, width, height };
    this.cdr.detectChanges();
  }

  private updateDrag(event: PointerEvent) {
    const rect = this.getImageRect();
    const box = this.dragIndex != null ? this.boxes[this.dragIndex]?.box : null;
    if (!rect || !box) return;
    this.noteMovement();
    box.left += (event.clientX - this.startX) / rect.width;
    box.top += (event.clientY - this.startY) / rect.height;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.constrain(box);
    this.cdr.detectChanges();
  }

  private updateResize(event: PointerEvent) {
    const rect = this.getImageRect();
    const box = this.dragIndex != null ? this.boxes[this.dragIndex]?.box : null;
    if (!rect || !box || !this.resizeDir) return;
    this.noteMovement();
    const dx = (event.clientX - this.startX) / rect.width;
    const dy = (event.clientY - this.startY) / rect.height;
    // Each corner drags its own edges; the opposite corner stays anchored.
    if (this.resizeDir.includes('r')) box.width += dx;
    if (this.resizeDir.includes('l')) { box.left += dx; box.width -= dx; }
    if (this.resizeDir.includes('b')) box.height += dy;
    if (this.resizeDir.includes('t')) { box.top += dy; box.height -= dy; }
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.constrain(box);
    this.cdr.detectChanges();
  }

  onDelete(index: number) {
    this.boxDeleted.emit(index);
  }

  // Explicit gesture to open the annotation popup for a box (double-click / right-click,
  // or double-tap / long-press on mobile). Allowed even in view mode; the popup itself
  // reflects edit permission.
  onBoxOpen(event: MouseEvent, index: number) {
    event.preventDefault();
    event.stopPropagation();
    if (this.isAddModifier(event)) return;
    this.boxOpened.emit(index);
  }

  private constrain(box: RiskBox) {
    box.width = Math.max(0.01, Math.min(box.width, 1));
    box.height = Math.max(0.01, Math.min(box.height, 1));
    box.left = Math.max(0, Math.min(box.left, 1 - box.width));
    box.top = Math.max(0, Math.min(box.top, 1 - box.height));
  }

  private getImageRect(): DOMRect | null {
    return this.imageContainer?.nativeElement.getBoundingClientRect() || null;
  }

  // Stable identity so the box elements are NOT recreated on every change detection —
  // otherwise the CDK popup loses its anchor element and mispositions.
  trackByIndex(index: number): number {
    return index;
  }

  onImageLoad(): void {
    const img = this.imageRef.nativeElement;
    if (!img.naturalWidth || !img.naturalHeight) {
      this.aspectClass = 'landscape';
      return;
    }
    const ratio = img.naturalWidth / img.naturalHeight;
    this.imageRatio = ratio;
    const epsilon = 0.02;
    if (Math.abs(ratio - 1) < epsilon) this.aspectClass = 'square';
    else this.aspectClass = ratio > 1 ? 'landscape' : 'portrait';
    this.cdr.detectChanges();
  }
}
